import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  DEFAULTS,
  TWITTER_HANDLE,
} from '../lib/seoConstants';

/**
 * SEO component — injects title, meta, Open Graph, Twitter, canonical, hreflang, and JSON-LD.
 *
 * Props:
 *  - titleAr / titleEn: page title per language (falls back to defaults)
 *  - descriptionAr / descriptionEn: meta description per language
 *  - path: current path (e.g. "/products/foo") used to build canonical + hreflang
 *  - image: absolute URL for OG image (defaults to site logo)
 *  - type: "website" | "article" | "product"
 *  - schemas: array of JSON-LD objects (Organization, Product, Article, BreadcrumbList, etc.)
 *  - noindex: boolean
 */
const SEO = ({
  titleAr,
  titleEn,
  descriptionAr,
  descriptionEn,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  schemas = [],
  noindex = false,
}) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const title = isAr
    ? titleAr || DEFAULTS.ar.title
    : titleEn || DEFAULTS.en.title;
  const description = isAr
    ? descriptionAr || DEFAULTS.ar.description
    : descriptionEn || DEFAULTS.en.description;
  const keywords = isAr ? DEFAULTS.ar.keywords : DEFAULTS.en.keywords;

  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const canonical = `${SITE_URL}${cleanPath}`;

  return (
    <Helmet>
      <html lang={isAr ? 'ar' : 'en'} dir={isAr ? 'rtl' : 'ltr'} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Canonical + hreflang */}
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="ar" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content={isAr ? 'ar_SA' : 'en_US'} />
      <meta property="og:locale:alternate" content={isAr ? 'en_US' : 'ar_SA'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {TWITTER_HANDLE && <meta name="twitter:site" content={TWITTER_HANDLE} />}

      {/* JSON-LD Structured Data */}
      {schemas.filter(Boolean).map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
